/* eslint-disable react-hooks/exhaustive-deps */
import { CloseOutlined, SyncOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Select, Tooltip, notification } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import dayjs from 'dayjs'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { NoteElement, TicketFilesVariantForm } from '@components'
import { TicketNotifContext } from '@contexts'
import {
  DeliveryNoteLabel,
  useConfigurationProperties,
  useDeliveryNotes,
  useDestinations,
  useMaterials,
  usePurchaseOrdersByMaterial,
} from '@hooks'
import {
  Material,
  Note,
  StatusTicketEnum,
  Ticket,
  TicketAssignment,
  TripTypeEnum,
  UpdateTicket,
} from '@types'
import { DeliveryNoteUtils, filterPurchaseOrdersByTripType, OtherUtils } from '@utils'

import { FlexRow, StyledForm, StyledRow } from '../../../../../components/StyledComponents'
import { StyledInput } from '../MobileFilters/components'
import { TIME_OPTIONS_CONFIG } from '../../../../../constants'
import { mapDestinationsToCombo } from '../../../../../utils/array-entity-to-combo'

let localizedFormat = require('dayjs/plugin/localizedFormat')
dayjs.locale('es')
dayjs.extend(localizedFormat)

type Props = {
  open: boolean
  onClose: () => void
  onOk: (payload: UpdateTicket, reset: () => void) => void
  loading: boolean
  ticket: Ticket
}

const editableStatuses = [StatusTicketEnum.CONFIRMED]
const destinationEditableStatuses = [StatusTicketEnum.CONFIRMED, StatusTicketEnum.TRANSIT]
const today = dayjs()
const endOfToday = today.endOf('d')

const handleFilterOptions = (input: string, option: any) => {
  if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
  return true
}

const AddAdditionalDataTicketModal: React.FC<Props> = ({
  open,
  onClose,
  onOk,
  loading,
  ticket,
}) => {
  // hooks
  const { t } = useTranslation()
  const deliveryNoteMinLength = DeliveryNoteUtils.DEFAULT_MIN_LENGTH
  const deliveryNoteMaxLength = DeliveryNoteUtils.DEFAULT_MAX_LENGTH
  const deliveryNoteInvalidMessage = t(
    'tpl_router.tpl_dashboard.delivery_note_modal.errors.delivery_note_invalid_format',
    { min: deliveryNoteMinLength, max: deliveryNoteMaxLength },
  )
  const [form] = StyledForm.useForm()
  const typedDeliveryNote = Form.useWatch('number', form)
  const { requireTruckAndTrailer } = useConfigurationProperties()

  // states
  const [initialValues, setInitialValues] = useState({})
  const [selectedDocumentNumber, setSelectedDocumentNumber] = useState<string | undefined>(
    ticket.documentNumberId,
  )
  const [selectedMaterial, setSelectedMaterial] = useState<Material | undefined>(ticket.material)
  const [editable, setEditable] = useState<boolean>()
  const [enableDocumentAndMaterialEdit, setEnableDocumentAndMaterialEdit] = useState<boolean>()
  const [enableTicketSapEdit, setEnableTicketSapEdit] = useState<boolean>(true)
  const [plantId, setPlantId] = useState<string | undefined>(ticket.destination?.plantId)

  const canEditDestination = destinationEditableStatuses.includes(
    ticket.status.name as StatusTicketEnum,
  )

  // Same delivery-note rules as Admin: CONFIRMED or TRANSIT, withdrawal date not in the
  // future, and purchase order plus material present. A non-current PO (disabledAt)
  // must not disable this selector; OC and material stay editable in those statuses.
  const canEditDeliveryNote = useMemo(() => {
    const status = ticket.status.name
    const isStatusValid =
      status === StatusTicketEnum.CONFIRMED || status === StatusTicketEnum.TRANSIT
    const withdrawalDate = ticket.withdrawalDate ? dayjs(ticket.withdrawalDate) : null
    const isNotFutureDate = withdrawalDate
      ? withdrawalDate.startOf('day') <= dayjs().startOf('day')
      : false
    const documentNumberId = selectedDocumentNumber || ticket.documentNumberId
    const materialId = selectedMaterial?.idSap ?? selectedMaterial?.id ?? ticket.material?.idSap

    return isStatusValid && isNotFutureDate && !!documentNumberId && !!materialId
  }, [
    ticket.status.name,
    ticket.withdrawalDate,
    ticket.documentNumberId,
    ticket.material?.idSap,
    selectedDocumentNumber,
    selectedMaterial,
  ])

  // hooks with dependencies
  const { purchaseOrders } = usePurchaseOrdersByMaterial(
    ticket.supplier?.sapId,
    plantId || ticket.destination?.plantId,
    ticket.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const purchaseOrdersFilteredByTripType = useMemo(
    () =>
      filterPurchaseOrdersByTripType(
        purchaseOrders,
        ticket.tripType,
        selectedDocumentNumber || ticket.documentNumberId,
      ),
    [purchaseOrders, selectedDocumentNumber, ticket.documentNumberId, ticket.tripType],
  )

  const { materials, materialsOptionsSapCode } = useMaterials(
    selectedDocumentNumber,
    undefined,
    ticket.withdrawalDate ? new Date(ticket.withdrawalDate).toISOString() : undefined,
  )
  const { allDestinationsOptions, destinations } = useDestinations(ticket.supplier?.sapId)
  const {
    deliveryNotesOptions,
    isDeliveryNotesLoading,
    isSyncingDeliveryNotes,
    showDeliveryNoteNotFoundWarning,
    syncDeliveryNotes,
  } = useDeliveryNotes(
    selectedDocumentNumber,
    selectedMaterial?.idSap ?? selectedMaterial?.id,
    materials,
    {
      currentDeliveryNote:
        String(selectedDocumentNumber) === String(ticket.documentNumberId ?? '')
          ? ticket.deliveryNote
          : undefined,
      typedDeliveryNote,
    },
  )

  const TicketContext = useContext(TicketNotifContext)

  const driverData = ticket.carrier?.drivers !== null ? ticket.carrier?.drivers : null
  const truckData = ticket.carrier?.trucks !== null ? ticket.carrier?.trucks : null
  const trailersData = ticket.carrier?.trailers !== null ? ticket.carrier?.trailers : null
  const straightTruck = ticket.equipment?.straightTruck

  const clearDriverAndTruckError = () => {
    form.setFields([
      {
        name: 'truck',
        errors: [],
      },
    ])

    form.setFields([
      {
        name: 'driver',
        errors: [],
      },
    ])
  }
  const handleDriverChange = (selectedValue: any) => {
    const value = selectedValue as { value: number }
    const driver = ticket?.carrier?.drivers?.find(
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (driver: { id: number }) => driver.id === value?.value,
    )

    if (driver) {
      const preferredTruck = ticket?.carrier?.trucks?.find(
        (truck: { id: any }) => truck.id === driver.preferredTrucks?.[0]?.id,
      )
      const preferredTrailer = ticket?.carrier?.trailers?.find(
        (trailer: { id: any }) => trailer.id === driver.preferredTrailers?.[0]?.id,
      )

      if (preferredTruck?.available) {
        form.setFieldsValue({
          truck: {
            label: preferredTruck.equipmentType
              ? `${preferredTruck.licensePlate} - ${preferredTruck.description} - ${preferredTruck.equipmentType.nameShort}`
              : `${preferredTruck.licensePlate} - ${preferredTruck.description}`,
            value: preferredTruck.id,
          },
        })
      }

      if (preferredTrailer?.available) {
        form.setFieldsValue({
          trailer: {
            label: preferredTrailer.equipmentType
              ? `${preferredTrailer.licensePlate} - ${preferredTrailer.description} - ${preferredTrailer.equipmentType.nameShort}`
              : `${preferredTrailer.licensePlate} - ${preferredTrailer.description}`,
            value: preferredTrailer.id,
          },
        })
      }
    }
    clearDriverAndTruckError()
  }
  truckData?.sort((a, b) => {
    if (!a.available && b.available) {
      return -1
    }
    if (a.available && !b.available) {
      return 1
    }
    const labelA = a.equipmentType
      ? `${a.licensePlate} ${a.description} ${a.equipmentType.nameShort}`.toLowerCase()
      : `${a.licensePlate} ${a.description}`.toLowerCase()
    const labelB = b.equipmentType
      ? `${b.licensePlate} ${b.description} ${b.equipmentType.nameShort}`.toLowerCase()
      : `${b.licensePlate} ${b.description}`.toLowerCase()
    return labelA.localeCompare(labelB)
  })

  useEffect(() => {
    setEditable(editableStatuses.includes(ticket.status.name as StatusTicketEnum))
  }, [])

  useEffect(() => {
    setEnableDocumentAndMaterialEdit(
      !(
        ticket.tripType !== TripTypeEnum.FOB ||
        (!editable && ![StatusTicketEnum.TRANSIT].includes(ticket.status.name as StatusTicketEnum))
      ),
    )
  }, [editable])

  useEffect(() => {
    const withdrawalDate = dayjs(ticket.withdrawalDate)
    setEnableTicketSapEdit(
      ticket.status.name !== StatusTicketEnum.DELIVERED && withdrawalDate < endOfToday,
    )
  }, [ticket])

  useEffect(() => {
    const availableQuantity = ticket.sapItemSupplier?.AvailableQuantity
      ? `${ticket.sapItemSupplier?.AvailableQuantity} tn`
      : ''
    setSelectedMaterial(ticket.material)
    setSelectedDocumentNumber(ticket.documentNumberId)
    setPlantId(ticket.destination?.plantId)

    const subPositionDescription = ticket.ticketAssignments.find(
      ticketAssignment =>
        ticketAssignment.status.name === TicketAssignment.CONFIRMED &&
        ticketAssignment.sapItem !== null,
    )
    const date = dayjs(ticket.withdrawalDate)
    const timeOption = TIME_OPTIONS_CONFIG.find(({ intValue }) => intValue === date.hour())
    setInitialValues({
      origin: ticket.origin.tag,
      date: `${date.format('llll')}${timeOption ? ` ${t(timeOption.labelKey)}` : ''}`,
      supplier: ticket.supplier.businessName,
      destinationId: ticket.destination?.id,
      material: ticket.material.idSap,
      equipment: ticket.equipment.name,
      number: ticket.deliveryNote,
      ticketSap: ticket.ticketSap,
      comments: ticket.comments,
      driver: ticket.driver?.id
        ? {
            label: `${ticket.driver.stateIdNumber} - ${ticket.driver.firstName} - ${ticket.driver.lastName}`,
            value: ticket.driver.id,
          }
        : undefined,
      truck: ticket.truck?.id
        ? {
            label: `${ticket.truck.licensePlate} - ${ticket.truck.description}${
              ticket.truck.equipmentType ? ` - ${ticket.truck.equipmentType.nameShort}` : ''
            }`,
            value: ticket.truck.id,
          }
        : undefined,
      trailer: ticket.trailer?.id
        ? {
            label: `${ticket.trailer.licensePlate} - ${ticket.trailer.description}${
              ticket.trailer.equipmentType ? ` - ${ticket.trailer.equipmentType.nameShort}` : ''
            }`,
            value: ticket.trailer.id,
          }
        : undefined,
      documentNumberId: ticket.documentNumberId,

      subPosition:
        subPositionDescription !== undefined
          ? `# ${subPositionDescription?.sapItem.subPosition}-${subPositionDescription?.sapItem.description}`
          : 'N/A',

      availableQuantity,
    })
    setTimeout(() => form.resetFields(), 100)
  }, [open])

  const reset = useCallback(() => {
    setInitialValues({})
    setTimeout(() => form.resetFields(), 10)
    notification.destroy('validationError')
  }, [form])

  const handleTicketSapErrors = useCallback(() => {
    const { ticketSap } = form.getFieldsValue()

    if (!ticketSap || !/^\d+$/.test(ticketSap)) {
      form.setFields([
        {
          name: 'ticketSap',
          errors: [
            t('tpl_router.tpl_dashboard.delivery_note_modal.errors.ticket_sap_invalid_format'),
          ],
        },
      ])
    } else {
      form.setFields([
        {
          name: 'ticketSap',
          errors: [],
        },
      ])
    }
  }, [form, t])

  const proceed = useCallback(() => {
    const { number, ticketSap } = form.getFieldsValue()
    const deliveryNoteNumber = DeliveryNoteUtils.normalizeValue(number)
    if (ticketSap) handleTicketSapErrors()
    if (ticketSap && !deliveryNoteNumber && ticket.tripType === TripTypeEnum.FOB) {
      form.setFields([
        {
          name: 'number',
          errors: [
            t(
              'tpl_router.tpl_dashboard.delivery_note_modal.errors.delivery_note_required_with_ticket_sap',
            ),
          ],
        },
      ])
    }
    if (!ticketSap && deliveryNoteNumber && ticket.tripType === TripTypeEnum.FOB) {
      form.setFields([
        {
          name: 'ticketSap',
          errors: [
            t(
              'tpl_router.tpl_dashboard.delivery_note_modal.errors.ticket_sap_required_with_delivery_note',
            ),
          ],
        },
      ])
    }
    if (
      deliveryNoteNumber &&
      !DeliveryNoteUtils.isValidDeliveryNote(
        deliveryNoteNumber,
        deliveryNoteMinLength,
        deliveryNoteMaxLength,
      )
    ) {
      form.setFields([
        {
          name: 'number',
          errors: [deliveryNoteInvalidMessage],
        },
      ])
    }

    if (!form.getFieldsValue().driver || !form.getFieldsValue().driver.value) {
      form.setFields([
        {
          name: 'driver',
          errors: ['Por favor, selecciona un conductor.'],
        },
      ])
    } else {
      form.setFields([
        {
          name: 'driver',
          errors: [],
        },
      ])
    }

    if (
      (!form.getFieldsValue().truck || !form.getFieldsValue().truck?.value) &&
      (requireTruckAndTrailer || !!form.getFieldsValue().trailer?.value)
    ) {
      form.setFields([
        {
          name: 'truck',
          errors: ['Por favor, selecciona un camión.'],
        },
      ])
    } else {
      form.setFields([
        {
          name: 'truck',
          errors: [],
        },
      ])
    }

    if (
      !ticket.equipment.straightTruck &&
      (!form.getFieldsValue().trailer || !form.getFieldsValue().trailer?.value) &&
      (requireTruckAndTrailer || !!form.getFieldsValue().truck?.value)
    ) {
      form.setFields([
        {
          name: 'trailer',
          errors: ['Por favor, selecciona un acoplado.'],
        },
      ])
    } else {
      form.setFields([
        {
          name: 'trailer',
          errors: [],
        },
      ])
    }

    if (!form.getFieldsValue().material) {
      form.setFields([
        {
          name: 'material',
          errors: ['Por favor, selecciona un material.'],
        },
      ])
    } else {
      form.setFields([
        {
          name: 'material',
          errors: [],
        },
      ])
    }

    form
      .validateFields()
      .then(values => {
        const { truck, driver, trailer, observation, documentNumberId, destinationId } = values

        const hasErrors = Object.keys(values).some(key => form.getFieldError(key).length > 0)

        if (!hasErrors) {
          onOk(
            {
              ...(deliveryNoteNumber
                ? {
                    deliveryNote: {
                      number: deliveryNoteNumber,
                      observation: observation || undefined,
                    },
                  }
                : {}),
              driverId: driver.value,
              truckId: truck === undefined ? truck : truck.value,
              trailerId: trailer === undefined ? trailer : trailer.value,
              documentNumberId: documentNumberId ?? undefined,
              materialId: selectedMaterial?.id || ticket.material.id,
              destinationId: destinationId ?? ticket.destination?.id,
              ticketSap: ticketSap ?? undefined,
            },
            reset,
          )
        }
      })
      .catch(error => {
        console.log(error)
        notification.destroy('validationError')
        notification.error({
          message: t('globals.validation_error'),
          key: 'validationError',
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    onOk,
    reset,
    selectedMaterial?.id,
    t,
    ticket,
    deliveryNoteInvalidMessage,
    deliveryNoteMinLength,
    deliveryNoteMaxLength,
    handleTicketSapErrors,
  ])
  const handlePurchaseDocumentChange = (documentNumberId: string | undefined) => {
    setSelectedDocumentNumber(documentNumberId)
    clearMaterial()
    form.setFieldsValue({ documentNumberId })
  }

  const handleDestinationSelect = useCallback(
    (value: number | undefined) => {
      setSelectedDocumentNumber(undefined)
      setSelectedMaterial(undefined)
      form.setFieldsValue({
        documentNumberId: undefined,
        material: undefined,
        number: undefined,
      })
      const destinationPlantId = destinations?.find(
        destination => destination.id === value,
      )?.plantId
      setPlantId(destinationPlantId)
    },
    [destinations, form],
  )

  const clearNumber = useCallback(() => {
    form.setFieldsValue({ number: undefined })
  }, [form])

  const clearMaterial = useCallback(() => {
    setSelectedMaterial(undefined)
    form.setFieldsValue({ material: undefined })
    clearNumber()
  }, [clearNumber, form])
  const materialChange = useCallback(
    (value: number | undefined) => {
      const newSelectedMaterial = materials?.find(material => material.idSap === value)
      setSelectedMaterial(newSelectedMaterial)
      form.setFieldsValue({ material: newSelectedMaterial?.idSap })
    },
    [form, materials],
  )
  const handleDeliveryNoteChange = useCallback(
    (value: string | string[] | undefined) => {
      const normalized = DeliveryNoteUtils.normalizeValue(value)
      if (
        normalized &&
        !DeliveryNoteUtils.isValidDeliveryNote(
          normalized,
          deliveryNoteMinLength,
          deliveryNoteMaxLength,
        )
      ) {
        form.setFields([
          {
            name: 'number',
            errors: [deliveryNoteInvalidMessage],
          },
        ])
        return
      }
      form.setFields([{ name: 'number', errors: [] }])
      if (!normalized) return
      const materialForSelectedDeliveryNote = materials?.filter(material =>
        material.sapItemSupplier?.sapItemSupplierDeliveryNotes?.some(
          item => item.deliveryNote === normalized,
        ),
      )[0]
      if (materialForSelectedDeliveryNote?.idSap) {
        materialChange(materialForSelectedDeliveryNote.idSap)
      }
    },
    [
      materials,
      materialChange,
      form,
      deliveryNoteMinLength,
      deliveryNoteMaxLength,
      deliveryNoteInvalidMessage,
    ],
  )
  const handleMaterialChange = useCallback(
    (value: number | undefined) => {
      materialChange(value)
      clearNumber()
    },
    [clearNumber, materialChange],
  )

  return (
    <Modal
      className="add-additional-data-ticket-modal"
      open={open}
      keyboard
      maskClosable
      onCancel={() => {
        setInitialValues({})
        setTimeout(() => form.resetFields(), 10)
        onClose()
      }}
      title={[t('tpl_router.tpl_dashboard.delivery_note_modal.title'), ` ${TicketContext.state}`]}
      okText={t('tpl_router.tpl_dashboard.delivery_note_modal.button_ok')}
      cancelText={t('tpl_router.tpl_dashboard.accept_decline_modal.accept.button_cancel')}
      onOk={proceed}
      destroyOnClose
      confirmLoading={loading}
      closeIcon={<CloseOutlined />}
      okButtonProps={{ disabled: ticket.status.name === StatusTicketEnum.DELIVERED }}
    >
      <StyledForm form={form} initialValues={initialValues}>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            label={t('tpl_router.tpl_dashboard.columns.withdrawal_date')}
            name="date"
          >
            <Input disabled />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item label={t('tpl_router.tpl_dashboard.columns.origin_supplier')}>
            {ticket.origin.tag === ticket.supplier.businessName ? (
              <Input disabled value={`${ticket.origin.tag}`} />
            ) : (
              <Input disabled value={`${ticket.origin.tag} (${ticket.supplier.businessName})`} />
            )}
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            label={t('tpl_router.tpl_dashboard.columns.destination')}
            name="destinationId"
            rules={[
              {
                required: true,
                message: t('globals.obligatory_field'),
              },
            ]}
          >
            <Select
              showSearch
              allowClear
              filterOption={handleFilterOptions}
              disabled={!canEditDestination}
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              options={
                ticket.supplier?.sapId
                  ? mapDestinationsToCombo(destinations)
                  : allDestinationsOptions
              }
              onChange={handleDestinationSelect}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row">
          <StyledForm.Item label={t('tpl_router.tpl_dashboard.columns.equipment')} name="equipment">
            <Input disabled />
          </StyledForm.Item>
        </StyledRow>

        {ticket.comments && (
          <StyledRow className="carrier-form-row">
            <StyledForm.Item label={t('tpl_router.tpl_dashboard.columns.comments')} name="comments">
              <TextArea disabled rows={3} />
            </StyledForm.Item>
          </StyledRow>
        )}

        <StyledRow className="carrier-form-row hide-feedback">
          <StyledForm.Item
            label={t('tpl_router.tpl_dashboard.columns.purchase_order')}
            name="documentNumberId"
          >
            <Select
              showSearch
              disabled={!enableDocumentAndMaterialEdit}
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={
                ticket.tripType !== TripTypeEnum.FOB ? 'N/A' : t('globals.placeholder_select')
              }
              options={
                purchaseOrdersFilteredByTripType
                  ? purchaseOrdersFilteredByTripType.map(purchaseDocument => ({
                      label: `${purchaseDocument.DocumentNumberID} - ${OtherUtils.capitalize(
                        dayjs(purchaseDocument.ProposedDeliveryDate)
                          .format('MMM-YY')
                          .replace('.', ''),
                      )} - ${purchaseDocument.Inco1}/${purchaseDocument.Inco2}`,
                      value: purchaseDocument.DocumentNumberID,
                    }))
                  : undefined
              }
              onChange={handlePurchaseDocumentChange}
            />
          </StyledForm.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row">
          <StyledForm.Item
            label={t('tpl_router.tpl_dashboard.columns.sub_position')}
            name="subPosition"
          >
            <Input disabled className="sub-position-field" />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row disabled hide-feedback">
          <StyledForm.Item label={t('tpl_router.tpl_dashboard.columns.material')} name="material">
            <Select
              allowClear
              showSearch
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              disabled={!enableDocumentAndMaterialEdit}
              placeholder={t('globals.placeholder_select')}
              options={
                !enableDocumentAndMaterialEdit
                  ? [
                      {
                        label: `${ticket?.material.nameShort} ${ticket.material.sapCode.replace(
                          /^0+/,
                          '',
                        )}`,
                        value: ticket?.material.idSap,
                      },
                    ]
                  : materialsOptionsSapCode
              }
              onChange={handleMaterialChange}
            />
          </StyledForm.Item>
        </StyledRow>
        <StyledRow className="carrier-form-row hide-feedback">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.columns.driver')}
            name="driver"
            validateTrigger={['onChange', 'onBlur']}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              disabled={!editable}
              filterOption={(input, option) => {
                const driver = driverData?.find(d => d.id === option?.value)
                if (driver) {
                  const fullName =
                    `${driver.stateIdNumber} ${driver.firstName} ${driver.lastName}`.toLowerCase()
                  return fullName.includes(input.toLowerCase())
                }
                return false
              }}
              options={driverData?.map(driver => ({
                label: driver.available
                  ? `${driver.stateIdNumber} - ${driver.firstName} ${driver.lastName}`
                  : `${driver.stateIdNumber} - ${driver.firstName} ${driver.lastName} (Ocupado)`,
                value: driver.id,
                available: driver.available,
                disabled: !driver.available,
              }))}
              labelInValue
              optionLabelProp="label"
              onChange={handleDriverChange}
            />
          </Form.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row hide-feedback">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.columns.truck')}
            name="truck"
            validateTrigger={['onChange', 'onBlur']}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              placeholder={t('globals.placeholder_select')}
              disabled={!editable}
              filterOption={(input, option) => {
                const truck = truckData?.find(tr => tr.id === option?.value)
                if (truck) {
                  const labelTruck = truck.equipmentType
                    ? `${truck.licensePlate} ${truck.description} ${truck.equipmentType.nameShort}`.toLowerCase()
                    : `${truck.licensePlate} ${truck.description}`.toLowerCase()
                  return labelTruck.includes(input.toLowerCase())
                }
                return false
              }}
              options={truckData?.map(truck => {
                if (truck.equipmentType) {
                  return {
                    label: truck.available
                      ? `${truck.licensePlate} - ${truck.description} - ${truck.equipmentType.nameShort}`
                      : `${truck.licensePlate} - ${truck.description} - ${truck.equipmentType.nameShort} (Ocupado)`,
                    value: truck.id,
                    available: truck.available,
                    disabled: !truck.available,
                  }
                }
                return {
                  label: truck.available
                    ? `${truck.licensePlate} - ${truck.description}`
                    : `${truck.licensePlate} - ${truck.description} (Ocupado)`,
                  value: truck.id,
                  available: truck.available,
                  disabled: !truck.available,
                }
              })}
              labelInValue
            />
          </Form.Item>
        </StyledRow>

        <StyledRow className="carrier-form-row hide-feedback">
          <Form.Item
            label={t('tpl_router.tpl_dashboard.columns.trailer')}
            name="trailer"
            validateTrigger={['onChange', 'onBlur']}
          >
            <Select
              showSearch
              allowClear
              getPopupContainer={(triggerNode: HTMLElement) =>
                triggerNode.parentNode as HTMLElement
              }
              disabled={straightTruck || !editable}
              placeholder={straightTruck ? null : t('globals.placeholder_select')}
              filterOption={(input, option) => {
                const trailer = trailersData?.find(tr => tr.id === option?.value)
                if (trailer) {
                  const labelTruck = trailer.equipmentType
                    ? `${trailer.licensePlate} ${trailer.description} ${trailer.equipmentType.nameShort}`.toLowerCase()
                    : `${trailer.licensePlate} ${trailer.description}`.toLowerCase()
                  return labelTruck.includes(input.toLowerCase())
                }
                return false
              }}
              options={trailersData?.map(trailer => {
                if (trailer.equipmentType) {
                  return {
                    label: trailer.available
                      ? `${trailer.licensePlate} - ${trailer.description} - ${trailer.equipmentType.nameShort}`
                      : `${trailer.licensePlate} - ${trailer.description} - ${trailer.equipmentType.nameShort} (Ocupado)`,
                    value: trailer.id,
                    available: trailer.available,
                    disabled: !trailer.available,
                  }
                }
                return {
                  label: trailer.available
                    ? `${trailer.licensePlate} - ${trailer.description}`
                    : `${trailer.licensePlate} - ${trailer.description} (Ocupado)`,
                  value: trailer.id,
                  available: trailer.available,
                  disabled: !trailer.available,
                }
              })}
              labelInValue
            />
          </Form.Item>
        </StyledRow>

        {(ticket.tripType === TripTypeEnum.FOB || ticket.tripType === TripTypeEnum.CFR) && (
          <StyledRow className="carrier-form-row">
            <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
              <StyledForm.Item
                label={
                  <DeliveryNoteLabel showNotFoundWarning={showDeliveryNoteNotFoundWarning}>
                    {t('tpl_router.tpl_dashboard.delivery_note')}
                  </DeliveryNoteLabel>
                }
                name="number"
                rules={[
                  {
                    validator: (_, value) => {
                      const normalized = DeliveryNoteUtils.normalizeValue(value)
                      if (!normalized) return Promise.resolve()
                      if (
                        !DeliveryNoteUtils.isValidDeliveryNote(
                          normalized,
                          deliveryNoteMinLength,
                          deliveryNoteMaxLength,
                        )
                      ) {
                        return Promise.reject(new Error(deliveryNoteInvalidMessage))
                      }
                      return Promise.resolve()
                    },
                  },
                ]}
              >
                <Select
                  allowClear
                  showSearch
                  getPopupContainer={triggerNode => triggerNode.parentNode}
                  disabled={!canEditDeliveryNote}
                  placeholder={t('globals.placeholder_select')}
                  options={deliveryNotesOptions}
                  loading={isDeliveryNotesLoading || isSyncingDeliveryNotes}
                  notFoundContent={t('suppliers_router.new_ticket_modal.no_delivery_notes')}
                  onChange={handleDeliveryNoteChange}
                  {...(selectedDocumentNumber || ticket.documentNumberId
                    ? {}
                    : { mode: 'tags' as const, maxCount: 1 })}
                />
              </StyledForm.Item>
              <Tooltip title={t('suppliers_router.new_ticket_modal.refresh_delivery_notes')}>
                <Button
                  type="primary"
                  icon={<SyncOutlined spin={isSyncingDeliveryNotes} />}
                  onClick={syncDeliveryNotes}
                  disabled={!canEditDeliveryNote || isSyncingDeliveryNotes}
                  loading={isSyncingDeliveryNotes}
                  className="contract-search-button"
                />
              </Tooltip>
            </FlexRow>
          </StyledRow>
        )}
        <StyledRow className="carrier-form-row ticket-sap-row">
          <StyledForm.Item
            label={t('tpl_router.tpl_dashboard.ticketSap')}
            name="ticketSap"
            hasFeedback
          >
            <StyledInput
              allowClear
              disabled={!enableTicketSapEdit}
              onChange={handleTicketSapErrors}
            />
          </StyledForm.Item>
        </StyledRow>
        {!!ticket.declinedLoadAt && !!ticket.declinedLoadType && (
          <>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item label={t('ticket_modal.load_rejected')} name="declinedLoad">
                <span className="ticket-field-value">
                  {dayjs(ticket.declinedLoadAt).format('DD/MM/YYYY HH:mm')}
                </span>
              </StyledForm.Item>
            </StyledRow>
            <StyledRow className="carrier-form-row">
              <StyledForm.Item
                label={t('ticket_modal.reason_for_rejection')}
                name="declinedLoadType"
              >
                <span className="ticket-field-value">
                  {t(`ticket_modal.${ticket.declinedLoadType.toLowerCase()}`)}
                </span>
              </StyledForm.Item>
            </StyledRow>
          </>
        )}
        <StyledRow className="carrier-form-row">
          <StyledForm.Item label={t('tpl_router.tpl_dashboard.observation')} name="observation">
            <TextArea allowClear maxLength={1200} rows={3} />
          </StyledForm.Item>
        </StyledRow>
        <TicketFilesVariantForm />
      </StyledForm>
      <StyledRow className="carrier-form-row">
        <StyledForm.Item label={t('tpl_router.tpl_dashboard.comment_modal.title')} name="comments">
          {ticket.observations.map((observation: Note) => (
            <NoteElement data={observation} />
          ))}
        </StyledForm.Item>
      </StyledRow>
    </Modal>
  )
}

export { AddAdditionalDataTicketModal }
